import React, { useState } from "react";
import { makeStyles } from "@material-ui/core/styles";
import Button from "@material-ui/core/Button";
import Dialog from "@material-ui/core/Dialog";
import MuiDialogTitle from "@material-ui/core/DialogTitle";
import MuiDialogContent from "@material-ui/core/DialogContent";
import MuiDialogActions from "@material-ui/core/DialogActions";
import IconButton from "@material-ui/core/IconButton";
import CloseIcon from "@material-ui/icons/Close";
import { useSnackbar } from "notistack";
import Form from "../../common/Form";
import API from "../../../utils/axios";
import { capitalize } from "../../../utils/helpers";

const useStyles = makeStyles(theme => ({
  dialogTitle: {
    padding: theme.spacing(2.5, 2),
    "& .MuiTypography-root": {
      alignItems: "center",
      display: "flex",
      fontSize: "1.25rem",
      fontWeight: "500"
    },
    "& button": {
      marginRight: theme.spacing(2)
    }
  },
  dialogContent: {
    padding: theme.spacing(4)
  },
  btnDefault: {
    "&.MuiButton-root:first-of-type": {
      background: "none",
      fontSize: "0.875rem",
      lineHeight: "1rem",
      padding: theme.spacing(1.25, 1)
    }
  },
  btnContained: {
    textTransform: "none",
    padding: theme.spacing(1.25, 2),
    fontSize: "0.875rem",
    lineHeight: "1rem",
    "&:hover": {
      backgroundColor: "rgb(13, 28, 177) !important"
    }
  },
  paper: {
    width: 464
  }
}));

export default function FormModal({
  open,
  handleClose,
  app,
  apps,
  setApps,
  setLoading
}) {
  const classes = useStyles();
  const { enqueueSnackbar } = useSnackbar();

  let form;

  const [inputValues, setInputValues] = useState({
    wpConsumerKey: "",
    wpConsumerSecret: "",
    wpLink: "",
    rentmanApiKey: "",
    shopifyApiKey: "",
    shopifyPassword: "",
    shopifyLink: ""
  });

  const [errorValues, setErrorValues] = useState({
    wpConsumerKey: "",
    wpConsumerSecret: "",
    wpLink: "",
    rentmanApiKey: "",
    shopifyApiKey: "",
    shopifyPassword: "",
    shopifyLink: ""
  });

  const handleChange = e => {
    setInputValues({ ...inputValues, [e.target.name]: e.target.value });
    // if (e.target.value.trim()) setSaveDisabled(false);
  };

  const handleSubmit = () => {
    let body, isValid;

    const handleValidate = fields => {
      setErrorValues({});
      let fieldsAreValid = true;
      const errorValues = {};
      Object.keys(fields).forEach(field => {
        if (!inputValues[field]) {
          fieldsAreValid = false;
          errorValues[field] = "Please fill in this field";
        }
      });

      setErrorValues(errorValues);

      return fieldsAreValid;
    };

    switch (app) {
      case "WooCommerce": {
        const { wpConsumerKey, wpConsumerSecret, wpLink } = inputValues;

        isValid = handleValidate({
          wpConsumerKey,
          wpConsumerSecret,
          wpLink
        });

        body = {
          name: app,
          api_username: wpConsumerKey,
          api_password: wpConsumerSecret,
          api_base_endpoint: wpLink
        };
        break;
      }
      case "Rentman": {
        const { rentmanApiKey } = inputValues;

        isValid = handleValidate({ rentmanApiKey });

        body = {
          name: app,
          api_key: rentmanApiKey
        };
        break;
      }
      case "Shopify": {
        const { shopifyApiKey, shopifyPassword, shopifyLink } = inputValues;

        isValid = handleValidate({
          shopifyApiKey,
          shopifyPassword,
          shopifyLink
        });

        body = {
          name: app,
          api_key: shopifyApiKey,
          api_password: shopifyPassword,
          api_base_endpoint: shopifyLink
        };
        break;
      }
      default:
        // do nothing;
        break;
    }

    if (!isValid) return;

    handleClose();
    setLoading(true);

    API.post("/v1/apps", body)
      .then(res => {
        setApps([...apps, res.data]);
        enqueueSnackbar(`${capitalize(res.data.name)} connected.`);
      })
      .catch(error => {
        enqueueSnackbar("Something went wrong");
      })
      .finally(() => {
        setLoading(false);
        setInputValues(
          Object.keys(inputValues).map(value => (inputValues[value] = ""))
        );
      });
  };

  switch (app) {
    case "WooCommerce":
      form = (
        <Form
          data={[
            [
              {
                renderInputField: true,
                label: "Consumer key",
                name: "wpConsumerKey",
                type: "text",
                autoFocus: true
              }
            ],
            [
              {
                renderInputField: true,
                label: "Consumer secret",
                name: "wpConsumerSecret",
                type: "text"
              }
            ],
            [
              {
                renderInputField: true,
                label: "Link",
                name: "wpLink",
                type: "text"
              }
            ]
          ]}
          inputValues={inputValues}
          errorValues={errorValues}
          handleChange={handleChange}
        />
      );
      break;
    case "Rentman":
      form = (
        <Form
          data={[
            [
              {
                renderInputField: true,
                label: "API key",
                name: "rentmanApiKey",
                type: "text",
                autoFocus: true
              }
            ]
          ]}
          inputValues={inputValues}
          errorValues={errorValues}
          handleChange={handleChange}
        />
      );
      break;
    case "Shopify":
      form = (
        <Form
          data={[
            [
              {
                renderInputField: true,
                label: "API key",
                name: "shopifyApiKey",
                type: "text",
                autoFocus: true
              }
            ],
            [
              {
                renderInputField: true,
                label: "Password",
                name: "shopifyPassword",
                type: "password"
              }
            ],
            [
              {
                renderInputField: true,
                label: "Link",
                name: "shopifyLink",
                type: "text"
              }
            ]
          ]}
          inputValues={inputValues}
          errorValues={errorValues}
          handleChange={handleChange}
        />
      );
      break;
    default:
      return null;
  }

  return (
    <Dialog
      onClose={() => handleClose(inputValues, setInputValues)}
      aria-labelledby="customized-dialog-title"
      open={open}
      classes={{
        paper: classes.paper
      }}
      style={{ zIndex: 1500 }}
    >
      <MuiDialogTitle className={classes.dialogTitle}>
        <IconButton onClick={() => handleClose(inputValues, setInputValues)}>
          <CloseIcon />
        </IconButton>
        {app}
      </MuiDialogTitle>
      <MuiDialogContent className={classes.dialogContent} dividers>
        {form}
      </MuiDialogContent>
      <MuiDialogActions>
        <Button
          className={classes.btnDefault}
          onClick={() => handleClose(inputValues, setInputValues)}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          disableElevation
          className={classes.btnContained}
          onClick={handleSubmit}
          color="primary"
        >
          Connect
        </Button>
      </MuiDialogActions>
    </Dialog>
  );
}
